import { Box } from '@mui/material';
import React from 'react';

import routes from '@/assets/constants/routes-no.ts';
import { ArrowLink } from '@/competitions/_common/components/ArrowLink.tsx';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWithConfig } from '@/lib/api/requests.ts';
import { Timer, type TimerTheme } from '@/ui/Timer.tsx';

import { MAY_2025_CAMPAIGN_END_DATE } from '../../../config.ts';
import { CTA } from '../_components/CTA.tsx';
import { Section } from '../_components/Section.tsx';

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});
const dateStr = dateFormatter.format(MAY_2025_CAMPAIGN_END_DATE);

const timerTheme: TimerTheme = {
  backgroundColor: '#355383',
  digitsColor: '#FFFFFF',
  labelsColor: '#F0F4FA',
};

export type TimerSectionProps = {
  activeCompetition: CompetitionWithConfig | undefined;
};

export const TimerSection: React.FC<TimerSectionProps> = (props) => {
  return (
    <Section
      sx={{
        gap: '20px',
        flexDirection: { md: 'row' },
        flexWrap: { md: 'wrap', xl: 'initial' },
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          textAlign: { xs: 'center', xl: 'left' },
          flex: { xl: 1.15 },
          width: { md: '100%', xl: 'auto' },
          mb: { xs: 4, md: 2 },
          pr: { xl: 8 },
          mt: { xl: 4 },
        }}
      >
        <div className="mb-6 typography-v1-h1">
          Våkn opp <span className="text-[#18A0FB]">gjeldfri</span>!
        </div>

        <p className="mx-auto mb-8 max-w-[70ch] typography-v1-body3">
          Vi mener at langt flere kan få lavere rente på sine forbrukslån og kredittkort - men de
          vet det ikke enda. For å få enda flere til å sjekke renten sin, og om de kan få lavere
          rente har vi satt i gang en trekning vi håper skaper oppmerksomhet.
          <br />
          <br />
          For å delta i trekningen må du sjekke om du kan få lavere rente på gjelden din. Det gjør
          vi fordi vi ønsker at enda flere får en oversikt over sin gjeld, hva de betaler i renter,
          og ikke minst - om de kan få lavere rente.
          <br />
          <br />
          Med lavere rente kan det være tusenvis av kroner å spare!
        </p>

        <div className="flex items-end gap-8">
          <CTA
            activeCompetition={props.activeCompetition}
            onClick={() => {
              sendUserPortalEvent('may25_competition_cta_timer_clicked');
            }}
          />
        </div>
      </Box>
      <div className="mx-auto flex max-w-sm flex-1 flex-col rounded-lg bg-[#EFF7FF] p-6 md:max-w-md lg:mx-0 lg:max-w-xl">
        <p className="mb-4 typography-v1-h2">Premie</p>
        <p className="mb-2 typography-v1-h3">
          Vinn en ferie til en verdi av{' '}
          <span className="mt-3 mb-2 block text-[2.5rem] font-bold md:text-[3rem]">50 000 kr</span>
        </p>
        <p className="typography-v1-h4">i tillegg til å få slettet inntil</p>
        <p className="-my-1 block text-[2.5rem] font-bold md:text-[3rem]">500 000 kr</p>
        <p className="mb-4 typography-v1-h4">av din rentebærende forbruks- og kredittkortgjeld</p>

        <p className="mt-auto mb-4 typography-v1-body3">
          Vi nedbetaler din forbruks- og kredittkortgjeld dersom du vinner.
          <br />
          Maks. 500&nbsp;000&nbsp;kr
        </p>

        <ArrowLink
          to={routes.MAY_2025_COMPETITION_TERMS}
          sx={{ display: 'block', my: 2, mt: { sm: 3 } }}
        >
          Vilkår og betingelser
        </ArrowLink>
      </div>
      <div className="mx-auto flex max-w-sm flex-1 flex-col rounded-lg bg-v1-background-200 p-6 text-white md:max-w-md lg:mx-0 lg:max-w-xl">
        <p className="typography-v1-h2">Trekningsdato</p>
        <Box
          component="p"
          className="mb-4 typography-v1-display"
          sx={{ fontSize: { lg: '3.812rem' } }}
        >
          {dateStr}
        </Box>
        <Timer date={MAY_2025_CAMPAIGN_END_DATE} theme={timerTheme} />

        <p className="mt-6 mb-4 typography-v1-body3 md:mt-8 xl:mt-12">
          Vinnerne kontaktes via e-post og mobil.
          <br />
          Vi tar hensyn til personvern og publiserer ikke vinnere uten samtykke.
        </p>

        <ArrowLink
          to={routes.MAY_2025_COMPETITION_TERMS}
          sx={{ display: 'block', my: 2, mt: { sm: 3 } }}
        >
          Vilkår og betingelser
        </ArrowLink>
      </div>
    </Section>
  );
};
